/**
 * roleColumns.tsx
 * Sovereign Emerald — Antd v6 column definitions + RoleToggleStatusCell for
 * the redesigned Roles list table (DataTableShell).
 *
 * Exports:
 *   RoleColumnHandlers     — handler bag consumed by buildRoleColumns
 *   buildRoleColumns       — ColumnsType<Role> factory
 *   RoleToggleStatusCell   — confirm-modal toggle component (disabled for isDefault roles)
 */

import React, { useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  ShieldCheck,
  Edit,
  Users,
  ListOrdered,
  CheckCircle,
  XCircle as XCircleIcon,
  X,
  AlertTriangle,
  Lock,
} from 'lucide-react';

import type { Role } from '@/types/role';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';

// ---------------------------------------------------------------------------
// 1. RoleColumnHandlers
// ---------------------------------------------------------------------------

export interface RoleColumnHandlers {
  onEdit: (role: Role) => void;
  onViewUsers: (role: Role) => void;
  onPermissions: (role: Role) => void;
  onMenuOrder: (role: Role) => void;
  /** Render the status-toggle cell. Typically: (r) => <RoleToggleStatusCell role={r} onConfirm={...} /> */
  toggleCell: (role: Role) => React.ReactNode;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Department pill */
function DeptPill({ dept }: { dept: string | undefined }) {
  if (!dept) return <span style={{ color: SOVEREIGN.kpiCaption }}>—</span>;
  const colorMap: Record<string, { bg: string; border: string; color: string }> = {
    OMS: { bg: 'rgba(51,146,116,0.07)', border: 'rgba(51,146,116,0.22)', color: SOVEREIGN.bottle },
    AMS: { bg: 'rgba(107,90,138,0.07)', border: 'rgba(107,90,138,0.22)', color: '#4B3B78' },
    EPC: { bg: 'rgba(201,169,97,0.10)', border: 'rgba(201,169,97,0.30)', color: '#7A5A20' },
  };
  const style = colorMap[dept] ?? { bg: 'rgba(124,138,130,0.07)', border: 'rgba(124,138,130,0.18)', color: SOVEREIGN.kpiCaption };
  return (
    <span
      className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded"
      style={{ background: style.bg, border: `1px solid ${style.border}`, color: style.color, letterSpacing: '0.03em' }}
    >
      {dept}
    </span>
  );
}

/** Status pill */
function StatusPill({ isActive }: { isActive: boolean }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded"
      style={
        isActive
          ? { background: 'rgba(31,90,71,0.07)', border: `1px solid rgba(31,90,71,0.20)`, color: SOVEREIGN.bottle }
          : { background: 'rgba(124,138,130,0.07)', border: `1px solid rgba(124,138,130,0.18)`, color: SOVEREIGN.kpiCaption }
      }
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        aria-hidden
        style={{ background: isActive ? SOVEREIGN.bottle : SOVEREIGN.kpiCaption }}
      />
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

/** Tiny action icon button — Sovereign hover style */
function ActionBtn({
  title,
  onClick,
  disabled,
  children,
  hoverColor = SOVEREIGN.bottle,
  hoverBg = 'rgba(31,90,71,0.07)',
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  hoverColor?: string;
  hoverBg?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center w-7 h-7 rounded transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
      style={{ color: SOVEREIGN.kpiCaption, border: '1px solid transparent' }}
      onMouseEnter={(e) => {
        if (!disabled) {
          const el = e.currentTarget as HTMLButtonElement;
          el.style.color = hoverColor;
          el.style.borderColor = SOVEREIGN.cardBorder;
          el.style.background = hoverBg;
        }
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.color = SOVEREIGN.kpiCaption;
        el.style.borderColor = 'transparent';
        el.style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// 2. buildRoleColumns
// ---------------------------------------------------------------------------

export function buildRoleColumns(handlers: RoleColumnHandlers): ColumnsType<Role> {
  const { onEdit, onViewUsers, onPermissions, onMenuOrder, toggleCell } = handlers;

  return [
    // ── Role Name ─────────────────────────────────────────────────────────
    {
      title: 'Role',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (_: unknown, role: Role) => {
        const isInactive = !role.isActive;
        return (
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span
                className={`text-sm font-semibold truncate ${isInactive ? 'line-through' : ''}`}
                style={{ color: isInactive ? SOVEREIGN.kpiCaption : SOVEREIGN.title }}
              >
                {role.name}
              </span>
              {(role as Role & { isDefault?: boolean }).isDefault && (
                <span
                  className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                  style={{
                    background: 'rgba(201,169,97,0.10)',
                    border: '1px solid rgba(201,169,97,0.28)',
                    color: SOVEREIGN.champagne,
                    letterSpacing: '0.03em',
                  }}
                >
                  <Lock size={8} aria-hidden />
                  Default
                </span>
              )}
              {role.isMobileUserRole && (
                <span
                  className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                  style={{
                    background: 'rgba(107,90,138,0.07)',
                    border: '1px solid rgba(107,90,138,0.20)',
                    color: '#4B3B78',
                    letterSpacing: '0.03em',
                  }}
                >
                  Mobile
                </span>
              )}
            </div>
            {role.description && (
              <span
                className="text-xs truncate max-w-xs"
                style={{ color: SOVEREIGN.kpiCaption }}
              >
                {role.description}
              </span>
            )}
          </div>
        );
      },
    },

    // ── Department ────────────────────────────────────────────────────────
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 120,
      render: (dept: string | undefined) => <DeptPill dept={dept} />,
    },

    // ── Users ─────────────────────────────────────────────────────────────
    {
      title: 'Users',
      dataIndex: 'userCount',
      key: 'userCount',
      width: 100,
      align: 'center',
      render: (_: unknown, role: Role) => {
        const count = role.userCount ?? 0;
        return (
          <button
            type="button"
            onClick={() => onViewUsers(role)}
            title="View users in this role"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded transition-colors duration-150"
            style={{
              background: 'rgba(51,146,116,0.07)',
              border: `1px solid rgba(51,146,116,0.18)`,
              color: SOVEREIGN.brand,
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.background = 'rgba(51,146,116,0.13)';
              el.style.borderColor = 'rgba(51,146,116,0.32)';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.background = 'rgba(51,146,116,0.07)';
              el.style.borderColor = 'rgba(51,146,116,0.18)';
            }}
          >
            <Users size={11} aria-hidden />
            {count}
          </button>
        );
      },
    },

    // ── Status ────────────────────────────────────────────────────────────
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => <StatusPill isActive={isActive} />,
    },

    // ── Actions ───────────────────────────────────────────────────────────
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 160,
      render: (_: unknown, role: Role) => (
        <div className="flex items-center justify-center gap-1.5">
          {/* Toggle status cell (wired by page) */}
          {toggleCell(role)}

          {/* Permissions — hidden for mobile-user roles */}
          {!role.isMobileUserRole && (
            <ActionBtn
              title={
                role.isDefault
                  ? 'View permissions (default roles are read-only)'
                  : 'Manage permissions'
              }
              onClick={() => onPermissions(role)}
              disabled={!role.isActive}
              hoverColor={SOVEREIGN.brand}
              hoverBg="rgba(51,146,116,0.07)"
            >
              <ShieldCheck size={15} aria-hidden />
            </ActionBtn>
          )}

          {/* Menu order — hidden for mobile-user roles */}
          {!role.isMobileUserRole && (
            <ActionBtn
              title={
                role.isDefault
                  ? 'Menu configuration is locked for default roles'
                  : 'Manage menu order'
              }
              onClick={() => onMenuOrder(role)}
              disabled={!role.isActive || !!role.isDefault}
              hoverColor={SOVEREIGN.brand}
              hoverBg="rgba(51,146,116,0.07)"
            >
              <ListOrdered size={15} aria-hidden />
            </ActionBtn>
          )}

          {/* Edit — hidden for mobile-user roles */}
          {!role.isMobileUserRole && (
            <ActionBtn
              title={
                role.isDefault
                  ? 'View role (default roles are locked — nothing can be changed)'
                  : 'Edit role'
              }
              onClick={() => onEdit(role)}
              hoverColor={SOVEREIGN.bottle}
              hoverBg="rgba(31,90,71,0.07)"
            >
              <Edit size={15} aria-hidden />
            </ActionBtn>
          )}
        </div>
      ),
    },
  ];
}

// ---------------------------------------------------------------------------
// 3. RoleToggleStatusCell
// ---------------------------------------------------------------------------

export interface RoleToggleStatusCellProps {
  role: Role;
  /** Page wires this to activate/deactivate. The cell does NOT call the API itself. */
  onConfirm: () => Promise<void>;
  /** Pass true for isDefault roles to disable toggling. */
  disabled?: boolean;
}

export const RoleToggleStatusCell: React.FC<RoleToggleStatusCellProps> = ({
  role,
  onConfirm,
  disabled = false,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const isActive = role.isActive;
  const isDefaultRole = !!(role as Role & { isDefault?: boolean }).isDefault;
  const isDisabled = disabled || (isDefaultRole && isActive);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      toast.success(`Role ${isActive ? 'deactivated' : 'activated'} successfully`);
      setOpen(false);
    } catch {
      toast.error('Failed to update role status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Toggle track */}
      <button
        type="button"
        title={
          isDisabled
            ? 'Cannot deactivate default role'
            : `Click to ${isActive ? 'deactivate' : 'activate'}`
        }
        aria-label={`${isActive ? 'Deactivate' : 'Activate'} ${role.name}`}
        onClick={() => !isDisabled && setOpen(true)}
        disabled={isDisabled}
        className="relative inline-flex items-center w-10 h-5 rounded-full border select-none transition-all duration-300 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
        style={
          isActive
            ? { background: 'rgba(31,90,71,0.10)', borderColor: 'rgba(31,90,71,0.30)' }
            : { background: 'rgba(124,138,130,0.10)', borderColor: 'rgba(124,138,130,0.25)' }
        }
      >
        <span
          aria-hidden
          className="w-3 h-3 rounded-full transform transition-all duration-300"
          style={
            isActive
              ? { transform: 'translateX(18px)', background: SOVEREIGN.bottle }
              : { transform: 'translateX(4px)', background: SOVEREIGN.kpiCaption }
          }
        />
      </button>

      {/* Confirm dialog */}
      <AnimatePresence>
        {isOpen && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center"
            style={{ background: 'rgba(42,31,23,0.18)' }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setOpen(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="relative w-full max-w-md mx-4 rounded-xl overflow-hidden"
              style={{
                background: SOVEREIGN.cardBg,
                border: `1px solid ${SOVEREIGN.cardBorder}`,
                boxShadow: SOVEREIGN.cardShadow,
              }}
              role="dialog"
              aria-modal
              aria-labelledby="role-toggle-dialog-title"
            >
              {/* Header */}
              <div
                className="flex items-center justify-between px-5 py-4"
                style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    aria-hidden
                    style={
                      isActive
                        ? { background: 'rgba(159,18,57,0.06)', border: '1px solid rgba(159,18,57,0.18)' }
                        : { background: 'rgba(31,90,71,0.07)', border: `1px solid ${SOVEREIGN.cardBorder}` }
                    }
                  >
                    {isActive ? (
                      <XCircleIcon size={18} style={{ color: '#9F1239' }} aria-hidden />
                    ) : (
                      <CheckCircle size={18} style={{ color: SOVEREIGN.bottle }} aria-hidden />
                    )}
                  </div>
                  <div>
                    <h2
                      id="role-toggle-dialog-title"
                      className="text-base font-semibold"
                      style={{ color: SOVEREIGN.title }}
                    >
                      {isActive ? 'Deactivate Role' : 'Activate Role'}
                    </h2>
                    <p className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
                      {role.name}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  title="Close"
                  aria-label="Close dialog"
                  onClick={() => setOpen(false)}
                  disabled={isLoading}
                  className="inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors duration-150 disabled:opacity-50"
                  style={{
                    color: SOVEREIGN.kpiCaption,
                    border: `1px solid ${SOVEREIGN.cardBorder}`,
                    background: SOVEREIGN.inputBg,
                  }}
                >
                  <X size={14} aria-hidden />
                </button>
              </div>

              {/* Body */}
              <div className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    aria-hidden
                    style={{ background: 'rgba(201,169,97,0.08)', border: '1px solid rgba(201,169,97,0.24)' }}
                  >
                    <AlertTriangle size={14} aria-hidden style={{ color: SOVEREIGN.champagne }} />
                  </div>

                  <div className="flex-1">
                    <p className="text-sm leading-relaxed mb-3" style={{ color: SOVEREIGN.ink }}>
                      Are you sure you want to{' '}
                      <span className="font-semibold">{isActive ? 'deactivate' : 'activate'}</span>{' '}
                      <span className="font-semibold" style={{ color: SOVEREIGN.title }}>
                        {role.name}
                      </span>
                      ?
                    </p>

                    <div
                      className="px-3 py-2 rounded-lg text-xs leading-relaxed"
                      style={{
                        background: SOVEREIGN.inputBg,
                        border: `1px solid ${SOVEREIGN.innerRule}`,
                        color: SOVEREIGN.kpiCaption,
                      }}
                    >
                      {isActive
                        ? 'This role will no longer be available for assignment to users.'
                        : 'This role will become available for assignment to users.'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                className="flex items-center justify-end gap-2.5 px-5 py-4 rounded-b-xl"
                style={{ borderTop: `1px solid ${SOVEREIGN.innerRule}`, background: 'rgba(242,234,219,0.35)' }}
              >
                {/* Cancel */}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-150 disabled:opacity-50"
                  style={{ color: SOVEREIGN.ink, background: SOVEREIGN.cardBg, border: `1px solid ${SOVEREIGN.cardBorder}` }}
                  onMouseEnter={(e) => {
                    if (!isLoading) (e.currentTarget as HTMLButtonElement).style.borderColor = SOVEREIGN.inputHoverBorder;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = SOVEREIGN.cardBorder;
                  }}
                >
                  Cancel
                </button>

                {/* Confirm */}
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-150 disabled:opacity-60"
                  style={
                    isActive
                      ? {
                          background: isLoading ? '#9F1239cc' : '#9F1239',
                          border: '1px solid rgba(159,18,57,0.45)',
                          boxShadow: '0 4px 10px -6px rgba(159,18,57,0.45)',
                        }
                      : {
                          background: isLoading ? `${SOVEREIGN.btn}cc` : SOVEREIGN.btn,
                          border: `1px solid ${SOVEREIGN.btnBorder}`,
                          boxShadow: SOVEREIGN.btnShadow,
                        }
                  }
                >
                  {isLoading && (
                    <span
                      className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin"
                      aria-hidden
                    />
                  )}
                  {isActive ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
